import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import useWebSocket from "react-use-websocket";

import { Alert, Button, Flex, Typography } from "antd";
import { Loader } from "../components";

import { LEGALE_SOCKET_BASE_URL } from "../utils/constants";
import { SmartDocumentWordAddInSocketMessageTypeEnum, SocketStateEnum } from "../utils/enums";
import { getUserNameHelper } from "../utils/helpers";
import { fetchSmartDocumentDetailRequest } from "../services/apis/smart_document";

import LegaleLogo from "../assets/images/logo.png";

import "../assets/styles/views/home.scss";

const props = {
  userData: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
};

const HomeView = ({ userData, token }) => {
  const [status, setStatus] = useState("awaiting");
  const [error, setError] = useState(null);
  const [smartTemplate, setSmartTemplate] = useState(null);

  const {
    sendMessage,
    lastMessage: socketMessage,
    readyState: socketStatus,
  } = useWebSocket(`${LEGALE_SOCKET_BASE_URL}/add-in/word/${userData.email}/?platform=word`, {
    share: false,
    shouldReconnect: () => true,
    onError: (e) => console.log(e),
  });

  useEffect(() => {
    console.log("Notification socket status: ", SocketStateEnum[socketStatus]);
  }, [socketStatus]);

  useEffect(() => {
    sendMessage(
      JSON.stringify({ type: "word_edit_status", data: { status, template_id: smartTemplate?.id } })
    );
  }, [status]);

  useEffect(() => {
    const { type, data } = JSON.parse(socketMessage?.data || "{}");
    setError(null);
    if (type === SmartDocumentWordAddInSocketMessageTypeEnum.START_EDIT_IN_WORD) {
      startEditContentHandler(data);
    } else if (type === SmartDocumentWordAddInSocketMessageTypeEnum.GET_CONTENT_FROM_WORD) {
      sendContentToLegaleHandler(data);
    }
  }, [socketMessage]);

  const startEditContentHandler = (data) => {
    const { template_id } = data;
    setStatus("loading");
    fetchSmartDocumentDetailRequest(token, template_id)
      .then(async (res) => {
        setSmartTemplate(res.data);
        if (window.Office) {
          try {
            await window.Word.run(async (context) => {
              const body = context.document.body;

              body.insertHtml(res.data.content, "Replace");

              body.font.set({
                name: "Arial",
                size: 10.5,
              });

              await context.sync();
              setStatus("editing");
            });
          } catch (e) {
            setError(e.message);
            setStatus("awaiting");
          }
        }
      })
      .catch((e) => {});
  };

  const sendContentToLegaleHandler = async (data) => {
    const { template_id } = data;
    if (String(template_id) === String(smartTemplate?.id)) {
      if (window.Office) {
        try {
          await window.Word.run(async (context) => {
            const body = context.document.body;

            const htmlContent = body.getHtml();
            await context.sync();

            sendMessage(
              JSON.stringify({
                type: SmartDocumentWordAddInSocketMessageTypeEnum.SEND_CONTENT_TO_LEGALE,
                data: {
                  template_id: smartTemplate?.id,
                  content: htmlContent.value,
                },
              })
            );
          });
        } catch (e) {
          setError(e.message);
        }
      }
    }
  };

  const exitEditingHandler = () => {
    setStatus("awaiting");
    setSmartTemplate(null);
  };

  return (
    <Flex vertical className="home-view">
      <Flex justify="space-between" align="center">
        <Flex justify="center" className="mr-3">
          <img className="logo-img" src={LegaleLogo} alt="Legale" />
        </Flex>
        <div className="text-right">
          <Typography.Text type="secondary" className="m-0">
            You logged in as
          </Typography.Text>
          <br />
          <Typography.Paragraph className="mb-0 mt-0">
            {getUserNameHelper(userData)}
          </Typography.Paragraph>
        </div>
      </Flex>
      <Flex vertical justify="center" align="center" className="home-view-content">
        {Boolean(error) && (
          <Alert
            message={error}
            type="error"
            className="mb-3"
            closable
            onClose={() => setError(null)}
          />
        )}

        {status === "awaiting" && (
          <Typography.Text type="secondary">Waiting for the content...</Typography.Text>
        )}
        {status === "loading" && <Loader />}
        {status === "editing" && (
          <div>
            <Typography.Title className="text-center" level={4} type="secondary">
              You are editing Smart document #{smartTemplate?.id} -{" "}
              <Typography.Text type="success">{smartTemplate?.name}</Typography.Text>
            </Typography.Title>

            <Button
              type="primary"
              block
              onClick={() => sendContentToLegaleHandler({ template_id: smartTemplate?.id })}
            >
              Send content to Legale
            </Button>
            <Button type="text" className="mt-2" block onClick={() => exitEditingHandler()}>
              Exit editing
            </Button>
          </div>
        )}
      </Flex>
    </Flex>
  );
};

HomeView.propTypes = props;

export default HomeView;
